import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarGradientFallback, Button, initialsFor, SearchInput, Text } from "@athena/ui";
import { fullName, type ClientRecord } from "../helpers/client";

export type ClientOption = Pick<ClientRecord, "id" | "firstName" | "lastName">;

/**
 * How many matches show at once. The list sits in the middle of the log dialog, so a long one
 * pushes the questions below it out of view. Five keeps the dialog on one screen, and every extra
 * letter typed narrows the list — a program of hundreds is searched, not scrolled.
 */
const MATCH_LIMIT = 5;

interface ClientSearchFieldProps {
    /** For the visible label's `htmlFor`. */
    id: string;
    clients: ClientOption[];
    /** The picked client's id. */
    value?: string;
    onValueChange: (clientId: string | undefined) => void;
}

/**
 * Pick one person by typing part of their name: matches appear under the box as you type, and the
 * one picked replaces the box until "Change" puts it back. A search rather than a dropdown because
 * the program runs to hundreds of people.
 */
export function ClientSearchField({ id, clients, value, onValueChange }: ClientSearchFieldProps) {
    const { t } = useTranslation();
    const [
        query,
        setQuery,
    ] = useState<string>("");
    const selected = clients.find((client) => client.id === value);

    if (selected) {
        return (
            <div className="border-primary bg-card flex items-center gap-3 rounded-2xl border px-4 py-3">
                <ClientAvatar client={selected} />
                <Text weight="medium" className="text-text-heading min-w-0 flex-1 truncate">
                    {fullName(selected)}
                </Text>
                {/* Focus follows the swap, so a keyboard user is not dropped back at the top of the dialog. */}
                <Button
                    variant="link"
                    size="sm"
                    className="w-auto tracking-normal normal-case"
                    autoFocus
                    onClick={() => onValueChange(undefined)}
                >
                    {t("admin.alumni.logContact.changeClient")}
                </Button>
            </div>
        );
    }

    const searchTerm = query.trim().toLowerCase();
    const matches = searchTerm
        ? clients.filter((client) => fullName(client).toLowerCase().includes(searchTerm)).slice(0, MATCH_LIMIT)
        : [];

    return (
        <div className="flex flex-col gap-2">
            <SearchInput
                id={id}
                label={t("admin.alumni.logContact.clientLabel")}
                placeholder={t("admin.alumni.logContact.clientPlaceholder")}
                className="h-11 rounded-full"
                autoFocus={query !== ""}
                value={query}
                onValueChange={setQuery}
            />
            {searchTerm && matches.length > 0 ? (
                <ul
                    aria-label={t("admin.alumni.logContact.clientMatches")}
                    className="border-border bg-card overflow-hidden rounded-2xl border"
                >
                    {matches.map((client) => (
                        <li key={client.id} className="border-border border-t first:border-t-0">
                            <button
                                type="button"
                                className="hover:bg-accent focus-visible:ring-ring flex w-full items-center gap-3 px-4 py-3 text-left focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                                onClick={() => onValueChange(client.id)}
                            >
                                <ClientAvatar client={client} />
                                <Text as="span" weight="medium" className="text-text-heading">
                                    {fullName(client)}
                                </Text>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
            {searchTerm && matches.length === 0 ? (
                <Text size="sm" muted>
                    {t("admin.alumni.logContact.noClientMatch", { query: query.trim() })}
                </Text>
            ) : null}
        </div>
    );
}

function ClientAvatar({ client }: { client: ClientOption }) {
    return (
        <Avatar size="sm" className="shrink-0" aria-hidden>
            <AvatarGradientFallback>{initialsFor(client.firstName, client.lastName)}</AvatarGradientFallback>
        </Avatar>
    );
}
